import React, { useState, useEffect } from "react";
import { IMAGES, PROJECTS } from "../mock";
import { ButtonLink, Kicker, SectionHeading } from "../components/UI";
import { PageHero } from "./FindRenovate";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { X, ArrowRight } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { useSiteImages } from "../context/SiteImages";
import { api } from "../lib/api";

const FILTERS = ["All", "Whole Home", "Kitchen", "Bathroom", "Primary Suite", "Fixer-Upper Transformation"];

export default function Portfolio() {
  const ref = useReveal();
  const { simg } = useSiteImages();
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [text, setText] = useState({});

  useEffect(() => {
    api.get("/site-projects").then((res) => setText(res.data || {})).catch(() => {});
  }, []);

  // resolve admin-uploaded image overrides + editable text onto each project
  const projects = PROJECTS.map((p) => {
    const t = text[p.id] || {};
    return {
      ...p,
      name: t.name || p.name,
      location: t.location || p.location,
      type: t.type || p.type,
      challenge: t.challenge || p.challenge,
      vision: t.vision || p.vision,
      transformation: t.transformation || p.transformation,
      cover: simg(`project_${p.id}_cover`, p.cover),
      before: simg(`project_${p.id}_before`, p.before),
      after: simg(`project_${p.id}_after`, p.after),
      gallery: p.gallery.map((g, i) => simg(`project_${p.id}_gallery_${i}`, g)),
    };
  });
  const shown = filter === "All" ? projects : projects.filter((p) => p.type === filter);

  return (
    <div ref={ref}>
      <PageHero
        kicker="Portfolio"
        title={<>Every Home Has <span className="text-gold-soft italic">Potential.</span></>}
        sub="Explore transformations designed around better living."
        image={IMAGES.livingRoom1}
        imageKey="hero_portfolio"
      />

      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-3 mb-14 justify-center">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`px-6 py-2.5 font-sans text-[11px] tracking-wide-nav uppercase border transition-colors duration-300 ${filter === f ? "bg-navy text-cream border-navy" : "border-navy/20 text-navy hover:border-gold hover:text-gold"}`}>{f}</button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {shown.map((p) => (
              <button key={p.id} onClick={() => setActive(p)} className="reveal group text-left">
                <div className="img-zoom h-80 relative">
                  <img src={p.cover} alt={p.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/30 transition-colors duration-500 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center gap-2 font-sans text-[11px] tracking-wide-nav uppercase text-cream bg-navy/80 px-5 py-3">View Project <ArrowRight size={14} /></span>
                  </div>
                </div>
                <p className="font-sans text-[11px] tracking-luxe uppercase text-gold mt-5">{p.type} · {p.location}</p>
                <h3 className="font-serif text-2xl text-navy mt-1 group-hover:text-gold transition-colors duration-300">{p.name}</h3>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Project detail overlay */}
      {active && (
        <div className="fixed inset-0 z-[60] bg-navy/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setActive(null)}>
          <div className="bg-cream max-w-4xl w-full my-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-72 md:h-96">
              <img src={active.cover} alt={active.name} className="w-full h-full object-cover" />
              <button onClick={() => setActive(null)} className="absolute top-5 right-5 w-11 h-11 bg-cream/90 flex items-center justify-center text-navy hover:bg-gold transition-colors duration-300"><X size={22} /></button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy/90 to-transparent p-8">
                <p className="font-sans text-[11px] tracking-luxe uppercase text-gold-soft">{active.type} · {active.location}</p>
                <h2 className="font-serif text-4xl text-cream mt-1">{active.name}</h2>
              </div>
            </div>
            <div className="p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                {[["The Challenge", active.challenge], ["The Vision", active.vision], ["The Transformation", active.transformation]].map(([t, b]) => (
                  <div key={t}>
                    <h4 className="font-sans text-[11px] tracking-luxe uppercase text-gold mb-3">{t}</h4>
                    <p className="font-sans text-sm text-charcoal/70 leading-relaxed">{b}</p>
                  </div>
                ))}
              </div>
              <Kicker>Before + After</Kicker>
              <div className="mt-4 mb-10"><BeforeAfterSlider before={active.before} after={active.after} beforeLabel="Before" afterLabel="After" className="h-72 md:h-96" /></div>
              <div className="grid grid-cols-3 gap-4 mb-10">
                {active.gallery.map((g, i) => (<img key={i} src={g} alt="Project detail" className="w-full h-32 md:h-44 object-cover" />))}
              </div>
              <div className="text-center border-t border-cream-dark pt-10">
                <p className="font-serif text-2xl text-navy mb-6">Thinking about transforming your own home?</p>
                <div className="flex justify-center"><ButtonLink to="/contact" variant="navy">Start Your Project</ButtonLink></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="py-20 md:py-24 bg-cream-dark text-center">
        <div className="max-w-2xl mx-auto px-6">
          <SectionHeading kicker="Your Turn" title="Let's Create Your Transformation." center className="reveal" />
          <div className="reveal flex justify-center mt-8"><ButtonLink to="/contact" variant="navy">Start Your Project</ButtonLink></div>
        </div>
      </section>
    </div>
  );
}
